
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Box } from '@mui/material';
import WebsiteNavbar from '../../common/website-navbar';
import WebsiteFooter from '../../common/website-footer';
import { getConfig } from '../../common/env-config';
import { refreshToken } from '../../common/refresh-token-repo';
import ECLandingLocation from '../ec-landing-pages/ec-landing-location';
import { ECLocationBoxStyle } from '../../../mui/website-mui-style';

const DemoPage = () => {
    const { baseUrl } = getConfig();
    const [location, setLocation] = useState(null);
    
    // Initialize email and token when component mounts (same as "for business" flow)
    useEffect(() => {
        const defaultEmail = 'externalagent@gmail.com';
        
        // Always call the API to get fresh token (same as ec-signup-form)
        const getAccessToken = async () => {
            try {
                const response = await fetch(`${baseUrl}/internal/ec-leads/external-agent-token`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        email: defaultEmail
                    })
                });

                const data = await response.json();
                if (response.ok) {
                    // Store email, accessToken, and userId (same as ec-signup-form)
                    localStorage.setItem('email', defaultEmail);
                    localStorage.setItem("accessToken", data.accessToken);
                    if (data.userId) {
                        localStorage.setItem("userId", data.userId);
                    }
                } else {
                    console.error('Error fetching access token: ', data.message);
                }
            } catch (error) {
                console.error('Error fetching access token: ', error);
            }
        };

        // Always call the API when page loads (same as "for business")
        getAccessToken();
    }, [baseUrl]);
    
    // Form state - load from localStorage if available
    const [formData, setFormData] = useState(() => {
        const savedFormData = localStorage.getItem('demoFormData');
        return savedFormData
            ? JSON.parse(savedFormData)
            : {
                fullName: '',
                workEmail: '',
                website: '',
                company: '',
                phone: '',
                role: '',
                message: '',
                leadLocation: null,
                placeId: null,
                consent: false
            };
    });

    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [mobileNumberError, setMobileNumberError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [submitStatus, setSubmitStatus] = useState({
        type: null, // 'success' or 'error'
        message: ''
    });

    // Handle input changes (for regular inputs and location)
    const handleChange = (fieldName, value) => {
        // If called from event (regular input), extract name and value
        if (fieldName && fieldName.target) {
            const e = fieldName;
            const { name, value, type, checked } = e.target;
            
            // Clear errors when user starts typing
            if (name === 'workEmail') setEmailError("");
            if (name === 'phone') setMobileNumberError("");
            
            setFormData(prev => {
                const updatedData = {
                    ...prev,
                    [name]: type === 'checkbox' ? checked : value
                };
                
                // Save to localStorage and sessionStorage on every change
                localStorage.setItem('demoFormData', JSON.stringify(updatedData));
                sessionStorage.setItem('demoSessionFormData', JSON.stringify(updatedData));
                
                return updatedData;
            });
        } else {
            // Called from location component (fieldName is string, value is the value)
            setFormData(prev => {
                const updatedData = {
                    ...prev,
                    [fieldName]: value
                };
                
                // Save to localStorage and sessionStorage on every change
                localStorage.setItem('demoFormData', JSON.stringify(updatedData));
                sessionStorage.setItem('demoSessionFormData', JSON.stringify(updatedData));
                
                return updatedData;
            });
        }
        
        setIsFormDirty(true);
        
        // Clear status message when user starts typing
        if (submitStatus.type) {
            setSubmitStatus({ type: null, message: '' });
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        if (!formData.consent) {
            setSubmitStatus({
                type: 'error',
                message: 'Please agree to receive communications to continue.'
            });
            return;
        }

        setIsSubmitting(true);
        setIsFormDirty(false);
        setSubmitStatus({ type: null, message: '' });

        try {
            // Prepare API request data
            const contactNumber = formData.phone.replace(/\D/g, ''); // Remove non-digits

            const requestBody = {
                fullName: formData.fullName.trim(),
                contactNumber: contactNumber,
                contactEmail: formData.workEmail,
                companyName: formData.company,
                website: formData.website,
                role: formData.role,
                message: formData.message,
                leadLocation: formData.leadLocation,
                placeId: formData.placeId,
                source: 'Demo Page'
            };

            // Get stored access token (for tracking/logging purposes)
            const accessToken = localStorage.getItem('accessToken');
            
            // Prepare headers
            const headers = {
                'Content-Type': 'application/json'
            };
            
            // Add Authorization header if token exists
            if (accessToken) {
                headers['Authorization'] = `Bearer ${accessToken}`;
            }
            
            // Make API call (endpoint is public, but we send token for tracking)
            const response = await fetch(`${baseUrl}/internal/ec-leads/save-external`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(requestBody)
            });

            const responseData = await response.json();

            if (!response.ok) {
                if (responseData.message?.includes("Mobile")) {
                    setMobileNumberError("Mobile number already exist.");
                    setEmailError("");
                } else if (responseData.message?.includes("Email")) {
                    setEmailError("Email ID already exist. Please use a different email or log in.");
                    setMobileNumberError("");
                } else if (responseData.message?.includes("User")) {
                    setMobileNumberError("Mobile number already exist.");
                    setEmailError("Email ID already exist. Please use a different email or log in.");
                } else if (response.status === 499) {
                    refreshToken();
                } else {
                    setSubmitStatus({
                        type: 'error',
                        message: responseData.message || 'Failed to submit demo request. Please try again.'
                    });
                    console.error('Error saving demo request:', responseData.message);
                }
            } else {
                // Success - clear stored form data
                localStorage.removeItem('demoFormData');
                sessionStorage.removeItem('demoSessionFormData');
                
                setSubmitStatus({
                    type: 'success',
                    message: 'Demo request submitted successfully! Redirecting to schedule your slot...'
                });

                // Reset form
                setFormData({
                    fullName: '',
                    workEmail: '',
                    website: '',
                    company: '',
                    phone: '',
                    role: '',
                    message: '',
                    leadLocation: null,
                    placeId: null,
                    consent: false
                });
                
                setLocation(null);
                setMobileNumberError("");
                setEmailError("");

                setTimeout(() => {
                    window.location.href = 'https://outlook.office365.com/book/ProductDemo@ttlmedia.in/?ismsaljsauthenabled=true';
                }, 500);

                // Clear status message after 5 seconds
                setTimeout(() => {
                    setSubmitStatus({ type: null, message: '' });
                }, 5000);
            }
        } catch (error) {
            console.error('Error submitting demo request:', error);
            setSubmitStatus({
                type: 'error',
                message: 'An error occurred while submitting your request. Please try again later.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Form dirty state tracking with beforeunload warning (similar to ec-signup-form)
    useEffect(() => {
        if (isFormDirty) {
            const handleBeforeUnload = (e) => {
                const currentPath = window.location.pathname;
                if (isFormDirty && currentPath.includes("/demo")) {
                    const message = 'You have unsaved changes. Are you sure you want to leave?';
                    e.returnValue = message;
                    return message;
                }
            };

            const handleNavClick = (e) => {
                if (isFormDirty) {
                    const message = 'You have unsaved changes. Are you sure you want to leave?';
                    if (!window.confirm(message)) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                    } else {
                        setIsFormDirty(false);
                    }
                }
            };

            const handlePopState = (e) => {
                const currentPath = window.location.pathname;
                if (isFormDirty && currentPath.includes("/demo")) {
                    const message = 'You have unsaved changes. Are you sure you want to leave?';
                    const userConfirmed = window.confirm(message);
                    if (!userConfirmed) {
                        window.history.pushState(null, '', window.location.pathname);
                    } else {
                        setIsFormDirty(false);
                    }
                }
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            window.addEventListener('popstate', handlePopState);
            window.history.pushState(null, '', window.location.pathname);

            const navLinks = document.querySelectorAll('.nav-link');
            navLinks.forEach((link) => {
                link.addEventListener('click', handleNavClick);
            });

            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                window.removeEventListener('popstate', handlePopState);
                navLinks.forEach((link) => {
                    link.removeEventListener('click', handleNavClick);
                });
            };
        }
    }, [isFormDirty]);

    const fadeIn = {
        initial: { opacity: 0, y: 15 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.5 }
    };

    const stagger = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    return (
        <>
            <WebsiteNavbar />
            <div className="bg-[#FAF9FF] text-slate-900 font-sans min-h-screen pt-[160px] md:pt-[200px] pb-24">
            <style>
                {`
                    .glass-card { 
                        backdrop-filter: blur(12px); 
                        background: #FFFFFF;
                        border: 1px solid #E2E8F0;
                        box-shadow: 0 25px 50px -12px rgba(101, 48, 143, 0.08);
                    }
                    .title-gradient {
                        background: linear-gradient(135deg, #65308F 0%, #EE3788 100%);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                    }
                    .custom-input {
                        width: 100%;
                        border-radius: 6px;
                        border: 1px solid #94A3B8;
                        background: #FFFFFF;
                        padding: 0.7rem 1rem;
                        font-size: 0.8125rem;
                        font-weight: 500;
                        color: #1E293B;
                        outline: none;
                        transition: all 0.2s ease;
                        display: block;
                    }
                    .custom-input:focus {
                        border-color: #EE3788;
                        box-shadow: 0 0 0 3px rgba(238, 55, 136, 0.08);
                    }
                    .custom-input::placeholder {
                        color: #94A3B8;
                        font-weight: 400;
                    }
                    .form-label {
                        display: block;
                        font-size: 10px;
                        font-weight: 800;
                        color: #475569;
                        text-transform: uppercase;
                        letter-spacing: 0.075em;
                        margin-bottom: 0.4rem;
                        margin-left: 0.125rem;
                    }
                    /* Ensuring the grid doesn't squeeze elements */
                    .form-grid {
                        display: grid;
                        gap: 1.5rem; /* Increased vertical and horizontal gap */
                    }
                    @media (min-width: 640px) {
                        .form-grid-cols-2 {
                            grid-template-columns: repeat(2, minmax(0, 1fr));
                        }
                    }
                `}
            </style>

            {/* Background Decorative Elements */}
            <div className="fixed top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
                <div className="absolute top-[-20%] right-[-10%] w-[60%] h-[60%] bg-primary-100/40 rounded-full blur-[140px]"></div>
                <div className="absolute bottom-[-20%] left-[-10%] w-[60%] h-[60%] bg-secondary-300/20 rounded-full blur-[140px]"></div>
            </div>

            <main className="mx-auto max-w-7xl px-6 lg:px-12">
                <motion.div
                    initial="initial"
                    animate="animate"
                    variants={stagger}
                    className="grid gap-16 lg:grid-cols-2 lg:gap-24 items-start"
                >
                    {/* Left panel */}
                    <motion.section variants={fadeIn} className="flex flex-col pt-4">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-primary-200 text-primary-500 text-[10px] font-bold uppercase tracking-[0.1em] mb-6 w-fit shadow-sm">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary-500"></span>
                            </span>
                            Experience the Platform
                        </div>

                        <h1 className="text-30 md:text-40 xl:text-[3.5rem] font-black tracking-tight leading-[1.1] mb-6">
                            Request a <br className="hidden xl:block" />
                            <span className="title-gradient">personalized</span> <br className="hidden xl:block" />
                            25-minute call
                        </h1>

                        <p className="text-18 leading-relaxed text-gray-600 mb-12 max-w-lg font-medium opacity-90">
                            Discover how Oye Creators empowers brands to execute high-impact creator campaigns.
                        </p>

                        <div className="space-y-10">
                            <div className="flex items-center gap-3">
                                <div className="h-[1px] w-8 bg-primary-500/30"></div>
                                <h2 className="text-11 font-black text-secondary-400 uppercase tracking-widest">
                                    Why Oye Creators?
                                </h2>
                            </div>

                            <ul className="space-y-6">
                                {[
                                    { title: "Strategic Tour", text: "End-to-end workflow from discovery to ROI." },
                                    { title: "Operational Scale", text: "How we manage 1000s of creators monthly." },
                                    { title: "Real-time Metrics", text: "Detailed reporting on reach and resonance." }
                                ].map((item, index) => (
                                    <li key={index} className="flex gap-4 items-start">
                                        <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded bg-primary-50 text-primary-500 flex items-center justify-center border border-primary-100 shadow-sm">
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-14 font-bold text-gray-900">{item.title}</span>
                                            <span className="text-13 font-medium text-gray-500 leading-snug">{item.text}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.section>

                    {/* Right panel: Reserved Lead Form */}
                    <motion.section
                        variants={fadeIn}
                        className="relative"
                    >
                        <div className="glass-card rounded-[1.5rem] p-10 lg:p-14 w-full">

                            <div className="mb-10">
                                <h2 className="text-28 font-black tracking-tight text-gray-900 mb-2">Reserved Demo</h2>
                                <p className="text-14 text-gray-500 font-medium opacity-80">
                                    Secure your slot for a strategic consultation.
                                </p>
                            </div>

                            <form className="space-y-6" onSubmit={handleSubmit}>
                                {/* Status Message */}
                                {submitStatus.type && (
                                    <div className={`p-4 rounded-lg ${
                                        submitStatus.type === 'success' 
                                            ? 'bg-green-50 border border-green-200 text-green-800' 
                                            : 'bg-red-50 border border-red-200 text-red-800'
                                    }`}>
                                        <p className="text-sm font-medium">{submitStatus.message}</p>
                                    </div>
                                )}

                                {/* Full Name - Full Row */}
                                <div className="space-y-1.5">
                                    <label className="form-label" htmlFor="fullName">Full Name</label>
                                    <input 
                                        id="fullName" 
                                        name="fullName" 
                                        required 
                                        className="custom-input" 
                                        placeholder="Enter your full name"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Email/Website */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
                                    <div className="space-y-1.5">
                                        <label className="form-label" htmlFor="workEmail">Professional Email</label>
                                        <input 
                                            id="workEmail" 
                                            name="workEmail" 
                                            type="email" 
                                            required 
                                            className="custom-input" 
                                            placeholder="rahul@company.com"
                                            value={formData.workEmail}
                                            onChange={handleChange}
                                        />
                                        {emailError && (
                                            <small className="text-red-600 text-xs mt-1 block">
                                                {emailError}
                                            </small>
                                        )}
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="form-label" htmlFor="website">Company Website</label>
                                        <input 
                                            id="website" 
                                            name="website" 
                                            required 
                                            className="custom-input" 
                                            placeholder="www.yourcompany.com"
                                            value={formData.website}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                {/* Company Entity / Location */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
                                    <div className="space-y-1.5">
                                        <label className="form-label" htmlFor="company">Company Entity</label>
                                        <input 
                                            id="company" 
                                            name="company" 
                                            required 
                                            className="custom-input" 
                                            placeholder="Enter full company name"
                                            value={formData.company}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="form-label" htmlFor="location">
                                            Location<span className="text-red-600 ml-1">*</span>
                                        </label>
                                        <Box sx={ECLocationBoxStyle} className="w-full h-10">
                                            <ECLandingLocation 
                                                handleChange={handleChange} 
                                                locationDescription={formData.leadLocation} 
                                                location={location} 
                                                setLocation={setLocation} 
                                                className="" 
                                            />
                                        </Box>
                                    </div>
                                </div>

                                {/* Phone/Role */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
                                    <div className="space-y-1.5">
                                        <label className="form-label" htmlFor="phone">Contact Number</label>
                                        <input 
                                            id="phone" 
                                            name="phone" 
                                            required
                                            className="custom-input" 
                                            placeholder="+91 XXXX XXXX XX"
                                            value={formData.phone}
                                            onChange={handleChange}
                                        />
                                        {mobileNumberError && (
                                            <small className="text-red-600 text-xs mt-1 block">
                                                {mobileNumberError}
                                            </small>
                                        )}
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="form-label" htmlFor="role">Industry Role</label>
                                        <div className="relative group">
                                            <select 
                                                id="role" 
                                                name="role" 
                                                required 
                                                className="custom-input appearance-none cursor-pointer pr-10"
                                                value={formData.role}
                                                onChange={handleChange}
                                            >
                                                <option value="" disabled>Select Role</option>
                                                <option value="Brand Manager">Brand Manager</option>
                                                <option value="Marketing Lead">Marketing Lead</option>
                                                <option value="Agency Partner">Agency Partner</option>
                                                <option value="Founder / CEO">Founder / CEO</option>
                                            </select>
                                            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Objective - Full Row */}
                                <div className="space-y-1.5">
                                    <label className="form-label" htmlFor="message">Strategic Objective</label>
                                    <textarea 
                                        id="message" 
                                        name="message" 
                                        rows="3" 
                                        className="custom-input resize-none" 
                                        placeholder="What are your goals?"
                                        value={formData.message}
                                        onChange={handleChange}
                                    ></textarea>
                                </div>

                                <div className="flex items-start gap-4 p-5 rounded-lg bg-gray-50 border border-gray-100 mt-10">
                                    <div className="pt-0.5">
                                        <input 
                                            id="consent" 
                                            name="consent" 
                                            type="checkbox" 
                                            required 
                                            className="h-5 w-5 rounded border-gray-300 text-primary-500 focus:ring-primary-400 cursor-pointer" 
                                            checked={formData.consent}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <label htmlFor="consent" className="text-[10px] leading-relaxed text-gray-500 font-bold cursor-pointer hover:text-gray-700 transition-colors uppercase tracking-wider">
                                        I agree to receive communications. Read our
                                        <Link className="text-primary-500 font-extrabold hover:underline ml-1" to="/privacy-policy">Privacy Policy</Link>.
                                    </label>
                                </div>

                                <motion.button
                                    whileHover={!isSubmitting ? { scale: 1.01 } : {}}
                                    whileTap={!isSubmitting ? { scale: 0.99 } : {}}
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full rounded-lg py-4 text-15 font-black text-white shadow-lg transition-all border-none mt-6 ${
                                        isSubmitting 
                                            ? 'bg-primary-400 cursor-not-allowed opacity-70' 
                                            : 'bg-primary-500 cursor-pointer hover:bg-primary-600'
                                    }`}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Get Personalized Demo'}
                                </motion.button>
                            </form>
                        </div>
                    </motion.section>
                </motion.div>
            </main>
            </div>
            <WebsiteFooter />
        </>
    );
};

export default DemoPage;